import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from '../hooks/useTranslation';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Functional fallback rendered by the class boundary. Lives in a sub-component
 * so it can use the `useTranslation` hook (class components can't).
 */
function ErrorBoundaryFallback({ error }: { error: Error }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        fontFamily: 'system-ui, -apple-system, sans-serif',
        padding: '32px',
        textAlign: 'center',
        color: '#1a1a2e',
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
      <h1 style={{ fontSize: '20px', margin: '0 0 8px' }}>
        {t('errorBoundary.title')}
      </h1>
      <p
        style={{
          fontSize: '14px',
          color: '#6b7280',
          margin: '0 0 24px',
          maxWidth: '420px',
        }}
      >
        {t('errorBoundary.description')}{' '}
        <a href="mailto:support@flymind.travel" style={{ color: '#0ea5e9' }}>
          support@flymind.travel
        </a>
        .
      </p>
      <div style={{ display: 'flex', gap: '12px' }}>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#0ea5e9',
            color: 'white',
            border: 0,
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('errorBoundary.reload')}
        </button>
        <button
          onClick={() => {
            try {
              localStorage.clear();
              sessionStorage.clear();
            } catch {
              // ignore — storage is the problem we are recovering from
            }
            window.location.href = '/';
          }}
          style={{
            background: 'white',
            color: '#1a1a2e',
            border: '1px solid #d1d5db',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {t('errorBoundary.backToHome')}
        </button>
      </div>
      <p
        style={{
          fontSize: '11px',
          color: '#9ca3af',
          marginTop: '24px',
          maxWidth: '420px',
          wordBreak: 'break-word',
        }}
      >
        {t('errorBoundary.detail')}: {error.message}
      </p>
    </div>
  );
}

/**
 * Top-level error boundary. Without this, any uncaught exception thrown
 * during a render produces a blank white page. With it, the user sees a
 * recoverable message and we get a console log to diagnose with.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[FlyMind] Uncaught render error:', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <ErrorBoundaryFallback error={this.state.error} />;
  }
}
